import React, { Dispatch, SetStateAction } from 'react';
import { UnitBulkOperationDto } from 'src/SharedCustomHooks/UsePropertySuites/types';
import Button from 'src/components/Button';
import WalkthroughActivationSection from 'src/components/WalkthroughActivationSection/WalkthroughActivationSection';
import { ISuite } from 'src/components/SharedTypes';
import BulkWalkthroughsActivationList from 'src/components/BulkOperations/Walkthroughs/BulkWalkthroughActivationList/BulkWalkthroughsActivationList';
import UnitsSelectionGrid from 'src/components/BulkOperations/Walkthroughs/UnitsSelectionGrid/UnitsSelectionGrid';
import {
  ActivationActions,
  UnitActions,
  WalkthroughActions,
} from 'src/components/BulkOperations/Walkthroughs/BulkActivationWalkthroughsDialogue/BulkActivateWalkthroughsDialogue';
import { IWalkthroughModel } from 'src/services/client-types';
import { ICalcomConnectedCalendar } from 'src/pages/Calendar/CalcomIntegration/types';
import CalcomSchedulingConfirmationNote from 'src/pages/Calendar/CalcomIntegration/components/CalcomSchedulingConfirmationNote/CalcomSchedulingConfirmationNote';
import LoadingButton from 'src/components/BulkOperations/_Shared/BulkActionButton/LoadingButton';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import Callout from 'src/components/InformationCallout/Callout';
import { variables } from 'src/Tenants';
import './Bulk-Activate-Walkthroughs.scss';

interface IBulkActivateWalkthroughsProps {
  suites: UnitBulkOperationDto[];
  detailedSuites: ISuite[];
  onClose: () => void;
  selectedUnitIds: Set<string>;
  isLoadingUnit: string | null;
  isFetchingWalkthroughs: boolean;
  activateWalkthroughs: boolean;
  sendSchedulingEmail: boolean;
  selectedWalkthroughIds: Set<string>;
  walkthroughsByUnit: Map<string, IWalkthroughModel[]>;
  performBulkActivation: () => Promise<void>;
  unitActions: UnitActions;
  walkthroughActions: WalkthroughActions;
  activationActions: ActivationActions;
  isSubmitting: boolean;
  confirmActivation: () => Promise<void>;
  setShowConfirmation: Dispatch<SetStateAction<boolean>>;
  showConfirmation: boolean;
  error: string | null;
  clearError: () => void;
  propertyId: string;
  isCalcomConnected: boolean;
  connectedCalendars: ICalcomConnectedCalendar[];
  selectedBookingLinkId: string | null;
  onSelectBookingLink: (bookingLinkId: string | null) => void;
}

const BulkActivateWalkthroughs = ({
  suites,
  detailedSuites,
  onClose,
  selectedUnitIds,
  isLoadingUnit,
  isFetchingWalkthroughs,
  activateWalkthroughs,
  sendSchedulingEmail,
  selectedWalkthroughIds,
  walkthroughsByUnit,
  performBulkActivation,
  unitActions,
  walkthroughActions,
  activationActions,
  isSubmitting,
  confirmActivation,
  setShowConfirmation,
  showConfirmation,
  error,
  clearError,
  propertyId,
  isCalcomConnected,
  connectedCalendars,
  selectedBookingLinkId,
  onSelectBookingLink,
}: IBulkActivateWalkthroughsProps) => {
  const hasSelectedUnits: boolean = selectedUnitIds.size > 0;
  const suiteName: string = variables.suiteName.toLowerCase();

  const isMissingBookingLinkForCalComEmail: boolean =
    sendSchedulingEmail && isCalcomConnected && !selectedBookingLinkId;

  const isCalcomSchedulingActive: boolean =
    sendSchedulingEmail && isCalcomConnected && Boolean(selectedBookingLinkId);

  const isActivateDisabled: boolean =
    selectedWalkthroughIds.size === 0 ||
    !activateWalkthroughs ||
    isMissingBookingLinkForCalComEmail;

  return (
    <div className="bulk-activate-walkthroughs">
      {showConfirmation && (
        <ConfirmationModal
          title="Confirm Bulk Activation"
          message={
            <>
              You are about to activate{' '}
              <strong>
                {selectedWalkthroughIds.size}{' '}
                {selectedWalkthroughIds.size === 1
                  ? 'walkthrough'
                  : 'walkthroughs'}
              </strong>
              .
            </>
          }
          warningMessage={
            <>
              <strong>Please double-check your settings.</strong> Currently,
              activated walkthroughs must be managed on a per-{suiteName} basis.
            </>
          }
          confirmLabel="Confirm & Activate"
          cancelLabel="Go Back"
          onConfirm={confirmActivation}
          onCancel={() => {
            clearError();
            setShowConfirmation(false);
          }}
          isSubmitting={isSubmitting}
          variant="warning"
          confirmationWord="ADD"
        >
          {isCalcomSchedulingActive && <CalcomSchedulingConfirmationNote />}
          {error && <Callout variant="error" title={error} />}
        </ConfirmationModal>
      )}
      <div className="bulk-activate-form">
        {/* 1. Activation Options. */}
        <div className="bulk-activate-form-group">
          <div className="activation-header">
            <label className="bulk-activate-label">
              Activation Settings
              <span className="required-field-indicator">*</span>
            </label>
          </div>
          <WalkthroughActivationSection
            activateWalkthroughs={activateWalkthroughs}
            sendSchedulingEmail={sendSchedulingEmail}
            actions={activationActions}
            disabled={false}
            propertyId={propertyId}
            isCalcomConnected={isCalcomConnected}
            connectedCalendars={connectedCalendars}
            selectedBookingLinkId={selectedBookingLinkId}
            onSelectBookingLink={onSelectBookingLink}
          />
        </div>
        {/* 2. Units Selection Grid. */}
        <div className="bulk-activate-form-group">
          <label className="bulk-activate-label">
            Select {suiteName}
            <span className="required-field-indicator">*</span>
          </label>
          <p className="bulk-activate-helper-text">
            Select one or more {suiteName} to view and activate their pending
            walkthroughs
          </p>
          <UnitsSelectionGrid
            suites={suites}
            selectedUnitIds={selectedUnitIds}
            isLoadingUnit={isLoadingUnit}
            unitActions={unitActions}
            detailedSuites={detailedSuites}
            isFetchingWalkthroughs={isFetchingWalkthroughs}
            sendSchedulingEmail={sendSchedulingEmail}
          />
          {hasSelectedUnits && (
            <div className="selected-summary">
              <strong>
                Activating walkthroughs for {selectedUnitIds.size}{' '}
                {selectedUnitIds.size === 1 ? 'unit' : 'units'}
              </strong>
            </div>
          )}
        </div>
        {/* 3. Review & Activate List. */}
        {hasSelectedUnits && (
          <div className="bulk-activate-form-group">
            <label className="bulk-activate-label">
              Review & Select Walkthroughs
            </label>
            <p className="bulk-activate-helper-text">
              Review and select walkthroughs to be activated for the selected{' '}
              {suiteName}
            </p>
            <BulkWalkthroughsActivationList
              walkthroughsByUnit={walkthroughsByUnit}
              selectedWalkthroughIds={selectedWalkthroughIds}
              walkthroughActions={walkthroughActions}
              suites={suites}
              sendSchedulingEmail={sendSchedulingEmail}
            />
          </div>
        )}
      </div>
      <div className="bulk-activate-footer">
        <Button
          outline="gray"
          size="small"
          label="Cancel"
          onClick={onClose}
          disabled={isSubmitting}
        />
        <LoadingButton
          actionText="Activate Walkthroughs"
          selectedCount={selectedWalkthroughIds.size}
          isSubmitting={isSubmitting}
          disabled={isActivateDisabled}
          onClick={performBulkActivation}
        />
      </div>
    </div>
  );
};

export default BulkActivateWalkthroughs;
